import { zodResolver } from '@hookform/resolvers/zod';
import CustomNavbar from 'components/core/CustomNavbar';
import Footer from 'components/core/Footer';
import RecuperarSenhaModal from 'components/modal/RecuperarSenhaModal';
import { useState } from 'react';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FaRegUser } from 'react-icons/fa';
import { FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';
import { IoMdCheckmark } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { selectPatientLoading, signInPatient } from 'redux/features/auth/authSlice';
import { loginSchema } from 'schemas/acesso';
import history from 'services/history';
import styles from 'styles/styles.module.css';

export default function SignInPaciente() {
  const dispatch = useDispatch();
  const loading = useSelector(selectPatientLoading);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordModalTitle, setResetPasswordModalTitle] = useState('Recuperar senha');
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    defaultValues: { cpf: '', senha: '' },
  });

  const onSubmit = ({ cpf, senha }) => {
    dispatch(signInPatient({ cpf, senha }));
  };

  return (
    <>
      <div
        className={`${styles.homePageFonts} d-flex flex-column min-vh-100`}
        style={{
          background: 'linear-gradient(180deg, #f7fbff 0%, #f5f7fb 100%)',
        }}
      >
        <CustomNavbar />

        <main className="flex-grow-1 pt-4 pt-lg-5 pb-4">
          <Container className="px-3 px-lg-4 pt-2">
            <Row className="align-items-start">
              <Col lg={6} className="d-none d-lg-flex align-self-start">
                <div className="pe-lg-5">
                  <p
                    className="text-uppercase mb-3"
                    style={{ fontSize: '0.75rem', letterSpacing: '0.24em', color: '#d30a0a' }}
                  >
                    Área do Paciente
                  </p>
                  <h1 className="display-6 fw-normal mb-3">
                    Bem-vindo de volta.
                    <br />
                    <span className="d-block fs-3 text-muted mt-2">
                      Seu coração com <span className="text-primary">carinho</span>.
                    </span>
                  </h1>

                  <div className={styles.pulseWrap}>
                    <svg viewBox="0 0 620 56" preserveAspectRatio="none">
                      <line className={styles.pulseBaseline} x1="0" y1="28" x2="620" y2="28" />

                      <path
                        className={styles.pulseLine}
                        d="M0,28 L120,28 L145,28 L160,8 L178,48 L196,14 L214,28 L245,28 L470,28 L488,8 L506,48 L524,14 L542,28 L620,28"
                      />
                    </svg>
                  </div>

                  <p
                    className="text-muted mt-4 fw-light"
                    style={{
                      fontSize: '0.85rem',
                      maxWidth: '460px',
                      lineHeight: 1.7,
                    }}
                  >
                    Acesse suas informações de saúde de forma simples, segura e sempre que precisar.{' '}
                  </p>

                  <Card
                    className="border-0 rounded-4 shadow-sm mt-4"
                    style={{
                      maxWidth: '260px',
                      background: 'rgba(255, 255, 255, 0.92)',
                    }}
                  >
                    <Card.Body className="p-3 p-lg-4">
                      <div
                        className="text-uppercase mb-3 fw-semibold"
                        style={{
                          fontSize: '0.72rem',
                          letterSpacing: '0.18em',
                          color: '#1d5c8a',
                        }}
                      >
                        Acesso rápido
                      </div>

                      <div className="d-grid gap-2">
                        <div className="d-flex align-items-center gap-2 text-muted small fw-medium">
                          <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              width: 20,
                              height: 20,
                              background: '#d2e9e2',
                              color: '#1e8f71',
                              fontWeight: 700,
                            }}
                          >
                            <IoMdCheckmark />
                          </span>
                          Consulte seus exames
                        </div>

                        <div className="d-flex align-items-center gap-2 text-muted small fw-medium">
                          <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              width: 20,
                              height: 20,
                              background: '#d2e9e2',
                              color: '#1e8f71',
                              fontWeight: 700,
                            }}
                          >
                            <IoMdCheckmark />
                          </span>
                          Gerencie suas consultas
                        </div>

                        <div className="d-flex align-items-center gap-2 text-muted small fw-medium">
                          <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              width: 20,
                              height: 20,
                              background: '#d2e9e2',
                              color: '#1e8f71',
                              fontWeight: 700,
                            }}
                          >
                            <IoMdCheckmark />
                          </span>
                          Acesse documentos clínicos
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Col>

              <Col lg={6} className="d-flex justify-content-center align-items-start">
                <Card
                  className="border-0 shadow rounded-4 position-relative"
                  style={{
                    maxWidth: 430,
                    width: '100%',
                    marginTop: 0,
                    background: 'rgba(255, 255, 255, 0.96)',
                    border: '1px solid rgba(30, 143, 113, 0.12)',
                  }}
                >
                  <Card.Body className="p-4 p-lg-23">
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none d-inline-flex align-items-center gap-2 px-0 mb-3"
                      style={{
                        color: '#1d5c8a',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                      onClick={() => history.push('/')}
                    >
                      <FiArrowLeft size={16} />
                      Voltar
                    </button>

                    <div className="d-flex justify-content-center mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: 70,
                          height: 70,
                          background: '#d2e9e2',
                          color: '#1e8f71',
                        }}
                      >
                        <FaRegUser size={32} />
                      </div>
                    </div>

                    <h4 className="text-center mb-2" style={{ color: '#10242a' }}>
                      Acesso do Paciente
                    </h4>
                    <p
                      className="text-center mb-4"
                      style={{
                        color: '#5a6b76',
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                      }}
                    >
                      Entre com seu CPF e senha cadastrados na clínica.
                    </p>

                    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                      <Form.Group controlId="formBasicCPF" className="mb-3">
                        <Form.Label className="fw-semibold" style={{ color: '#10242a' }}>
                          CPF
                        </Form.Label>

                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          autoComplete="username"
                          placeholder="000.000.000-00"
                          maxLength={14}
                          isInvalid={Boolean(touchedFields.cpf && errors.cpf)}
                          style={{
                            borderRadius: 14,
                            borderColor: '#d7e4ec',
                            padding: '0.8rem 1rem',
                            background: '#f9fbfc',
                          }}
                          {...register('cpf')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cpf?.message}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group controlId="formBasicSenha" className="mb-3">
                        <Form.Label className="fw-semibold" style={{ color: '#10242a' }}>
                          Senha
                        </Form.Label>

                        <div className="position-relative">
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder="Sua senha"
                            isInvalid={Boolean(touchedFields.senha && errors.senha)}
                            style={{
                              borderRadius: 14,
                              borderColor: '#d7e4ec',
                              padding: '0.8rem 3rem 0.8rem 1rem',
                              background: '#f9fbfc',
                            }}
                            {...register('senha')}
                          />

                          <button
                            type="button"
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            aria-pressed={showPassword}
                            className="btn btn-link position-absolute top-50 end-0 translate-middle-y px-3"
                            style={{
                              color: '#1d5c8a',
                              textDecoration: 'none',
                            }}
                            onClick={() => setShowPassword((current) => !current)}
                          >
                            {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                          </button>
                        </div>
                        <Form.Control.Feedback
                          type="invalid"
                          className={touchedFields.senha && errors.senha ? 'd-block' : undefined}
                        >
                          {errors.senha?.message}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <div
                        className="d-flex justify-content-end mb-3"
                        style={{ marginTop: '-8px' }}
                      >
                        <button
                          type="button"
                          className={`btn btn-link p-0 ${styles.resetPasswordLink}`}
                          onClick={() => {
                            setResetPasswordModalTitle('Recuperar senha');
                            setShowResetPasswordModal(true);
                          }}
                        >
                          Esqueci minha senha
                        </button>
                      </div>

                      <Button
                        type="submit"
                        variant="success"
                        className={`rounded-pill w-100 mb-2 border-0 mt-2 ${styles.cardBtn} ${styles.cardBtnGreen}`}
                        style={{
                          minHeight: '3rem',
                          padding: '0.85rem 1rem',
                          fontWeight: 700,
                          color: '#ffffff',
                        }}
                        disabled={loading || !isValid}
                      >
                        {loading ? (
                          <Spinner animation="border" size="sm" variant="light" />
                        ) : (
                          'Entrar'
                        )}
                      </Button>

                      <div className="d-flex align-items-center my-3">
                        <div style={{ flex: 1, height: 1, background: '#d9e4ec' }} />
                        <span
                          className="mx-3 text-uppercase"
                          style={{ color: '#7b8791', fontSize: '0.72rem' }}
                        >
                          ou
                        </span>
                        <div style={{ flex: 1, height: 1, background: '#d9e4ec' }} />
                      </div>

                      <Button
                        type="button"
                        className={`rounded-pill w-100 border-0 ${styles.cardBtn}`}
                        style={{
                          background: '#d2e9e2',
                          color: '#1e8f71',
                          padding: '0.85rem 1rem',
                          fontWeight: 700,
                        }}
                        onClick={() => {
                          setResetPasswordModalTitle('Primeiro acesso');
                          setShowResetPasswordModal(true);
                        }}
                      >
                        Primeiro acesso?
                      </Button>
                      <p
                        className="text-center mt-3 mb-0 fw-light text-secondary"
                        style={{
                          fontSize: '0.75rem',
                          lineHeight: 1.5,
                        }}
                      >
                        <span className="fw-normal">Problemas no acesso? </span>
                        Fale com nosso{' '}
                        <a
                          href="https://wa.link/7ulmr2"
                          target="_blank"
                          rel="noreferrer"
                          className="text-secondary"
                          style={{
                            textDecoration: 'underline',
                          }}
                        >
                          suporte
                        </a>
                        .
                      </p>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </main>

        <Footer />
      </div>

      <RecuperarSenhaModal
        showResetPasswordModal={showResetPasswordModal}
        setShowResetPasswordModal={setShowResetPasswordModal}
        userType="paciente"
        title={resetPasswordModalTitle}
      />
    </>
  );
}
